/**
 * Compare products carousel:
 * - Keeps matching fact rows aligned across cards at every viewport width
 * - Turns the card row into a swipeable/arrow-driven carousel whenever the cards overflow
 */
const COMPARE_ROWS = ["title", "best", "when", "specs", "cost", "flavors"];

class PdpSpecsCompare extends HTMLElement {
  #frame = 0;
  #timer = 0;
  #observer;
  #abortController;
  #controls = [];

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener("scroll", this.#onScroll, { passive: true, signal });
    window.addEventListener("resize", this.#onResize, { passive: true, signal });
    document.addEventListener("visibilitychange", this.#onResize, { signal });

    this.#controls = Array.from(
      (this.parentElement ?? document).querySelectorAll(
        `[data-compare-scroll][aria-controls="${this.id}"]`
      )
    );

    this.#controls.forEach((button) => {
      button.addEventListener(
        "click",
        () => this.#scrollByCard(button.dataset.compareScroll === "prev" ? -1 : 1),
        { signal }
      );
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.#scheduleUpdate());
    }

    this.#observer = new ResizeObserver(() => this.#scheduleUpdate());
    this.#observer.observe(this);

    this.querySelectorAll("img").forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", this.#onResize, { once: true, signal });
      }
    });

    this.#scheduleUpdate();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
  }

  get #items() {
    return Array.from(this.querySelectorAll(':scope > [role="listitem"]'));
  }

  get #maxScroll() {
    return Math.max(this.scrollWidth - this.clientWidth, 0);
  }

  /** Distance between two adjacent cards, so arrows advance exactly one card. */
  get #cardStep() {
    const items = this.#items;
    if (items.length > 1) {
      const step = Math.abs(items[1].offsetLeft - items[0].offsetLeft);
      if (step > 0) return step;
    }
    return this.clientWidth;
  }

  #onResize = () => this.#scheduleUpdate();

  #onScroll = () => this.#updateControls();

  #scrollByCard(direction) {
    this.scrollBy({
      left: this.#cardStep * direction,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  }

  #scheduleUpdate() {
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);

    // Background tabs never fire animation frames, so measure on a timeout instead.
    if (document.hidden) {
      this.#timer = setTimeout(() => this.#update(), 0);
      return;
    }

    this.#frame = requestAnimationFrame(() => {
      this.#frame = requestAnimationFrame(() => this.#update());
    });
  }

  #update() {
    this.#equalize();
    this.#updateControls();
  }

  /**
   * The controls are hidden by CSS while the host has no `is-scrollable` class,
   * so everything collapses to a plain row once all cards fit.
   */
  #updateControls() {
    const maxScroll = this.#maxScroll;
    const isScrollable = maxScroll > 1;
    const scrollLeft = Math.abs(this.scrollLeft);

    this.classList.toggle("is-scrollable", isScrollable);

    if (isScrollable) {
      this.setAttribute("tabindex", "0");
    } else {
      this.removeAttribute("tabindex");
    }

    this.#controls.forEach((button) => {
      if (button.dataset.compareScroll === "prev") {
        button.disabled = !isScrollable || scrollLeft <= 1;
      } else {
        button.disabled = !isScrollable || scrollLeft >= maxScroll - 1;
      }
    });
  }

  #equalize() {
    const cards = Array.from(this.querySelectorAll(".pdp-specs-compare__product"));

    this.querySelectorAll("[data-compare-row]").forEach((cell) => {
      cell.style.minHeight = "";
    });

    if (cards.length < 2) return;

    COMPARE_ROWS.forEach((row) => {
      const cells = cards
        .map((card) => card.querySelector(`[data-compare-row="${row}"]`))
        .filter(Boolean);

      const maxHeight = Math.max(...cells.map((cell) => cell.getBoundingClientRect().height), 0);

      if (maxHeight > 0) {
        cells.forEach((cell) => {
          cell.style.minHeight = `${Math.ceil(maxHeight)}px`;
        });
      }
    });
  }
}

if (!window.customElements.get("pdp-specs-compare")) {
  window.customElements.define("pdp-specs-compare", PdpSpecsCompare);
}
